/**
 * VariableDetailForm — in-panel auto-committing detail form for the master/detail
 * single-app editor (MEASURES / DIMENSIONS / VARIABLES tabs). Presentation only;
 * the data/edit model is unchanged — it dispatches field-level diffs to the parent
 * via `onChange` instead of a submit button.
 *
 * - Script-created variables are fully read-only.
 * - Prefill priority: existingCreate → existingChange + item → item.
 * - Only changed fields are emitted via `onChange`.
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Code } from "lucide-react";
import type { QlikVariable } from "@repo/shared";
import { Badge, Field, Input, Textarea } from "@h2j-data-team/ui";
import { QlikExpressionEditor } from "@/components/qlik/qlik-expression-editor";
import type { PendingVariableChange, PendingVariableCreate } from "@/stores/pending-changes";

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

type VariableChanges = {
  name?: string;
  definition?: string;
  comment?: string;
};

export interface VariableDetailFormProps {
  /** Effective base object. For a brand-new item the parent passes a blank template: { id: tempId, name: "" }. */
  item: QlikVariable;
  /** true when this item is a pending-create (keyed by tempId) */
  isCreate: boolean;
  /** Prefill for an existing update-type pending change */
  existingChange?: PendingVariableChange;
  /** Prefill for a pending-create */
  existingCreate?: PendingVariableCreate;
  /** false = all inputs disabled */
  canEdit: boolean;
  /**
   * Called on each field change/blur with a diff object containing only the
   * changed fields. Parent routes this to handleSaveVariableToCache (update)
   * or updateCreate (pending-create) or addCreate (brand-new first edit).
   */
  onChange: (changes: VariableChanges) => void;
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function getEffectiveValues(
  item: QlikVariable,
  isCreate: boolean,
  existingChange: PendingVariableChange | undefined,
  existingCreate: PendingVariableCreate | undefined,
): { name: string; definition: string; description: string } {
  if (isCreate && existingCreate) {
    return {
      name: existingCreate.name,
      definition: existingCreate.definition ?? "",
      description: existingCreate.comment ?? "",
    };
  }

  if (existingChange) {
    return {
      name: existingChange.changes.name ?? item.name,
      definition: existingChange.changes.definition ?? item.definition ?? "",
      description: existingChange.changes.comment ?? item.description ?? "",
    };
  }

  return {
    name: item.name,
    definition: item.definition ?? "",
    description: item.description ?? "",
  };
}

// -----------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------

export function VariableDetailForm({
  item,
  isCreate,
  existingChange,
  existingCreate,
  canEdit,
  onChange,
}: VariableDetailFormProps) {
  const { t } = useTranslation("single-app");
  const effective = getEffectiveValues(item, isCreate, existingChange, existingCreate);

  const [name, setName] = useState(effective.name);
  const [definition, setDefinition] = useState(effective.definition);
  const [description, setDescription] = useState(effective.description);

  // Re-initialise when the selected item changes, or the pending change/create
  // changes (e.g., cleared by "Discard all") — matching DimensionDetailForm.
  useEffect(() => {
    const e = getEffectiveValues(item, isCreate, existingChange, existingCreate);
    setName(e.name);
    setDefinition(e.definition);
    setDescription(e.description);
  }, [item, isCreate, existingChange, existingCreate]);

  const isReadOnly = item.isScriptCreated || !canEdit;

  // Build the baseline for diffing (what the server/create knows about)
  const baseline = getEffectiveValues(item, isCreate, existingChange, existingCreate);

  // Emit diff only for changed fields
  const emitChange = useCallback(
    (patch: Partial<{ name: string; definition: string; description: string }>) => {
      const changes: VariableChanges = {};

      const nextName = patch.name !== undefined ? patch.name : name;
      const nextDefinition = patch.definition !== undefined ? patch.definition : definition;
      const nextDescription = patch.description !== undefined ? patch.description : description;

      // Compare against the CURRENT effective baseline (before this edit)
      if (nextName !== baseline.name) changes.name = nextName;
      if (nextDefinition !== baseline.definition) changes.definition = nextDefinition;
      if (nextDescription !== baseline.description) changes.comment = nextDescription;

      if (Object.keys(changes).length > 0) {
        onChange(changes);
      }
    },
    [name, definition, description, baseline, onChange],
  );

  const handleNameBlur = () => {
    if (!isReadOnly) emitChange({ name });
  };

  const handleDescriptionBlur = () => {
    if (!isReadOnly) emitChange({ description });
  };

  // Definition editor calls onChange as the user types (debounced by the
  // expression editor itself); we commit on every change event.
  const handleDefinitionChange = (val: string) => {
    setDefinition(val);
    if (!isReadOnly) emitChange({ definition: val });
  };

  const typeLabel = item.isScriptCreated
    ? t("variables.typeScript")
    : t("variables.typeUserDefined");

  return (
    <div className="flex flex-col gap-5">
      {/* Type badge + read-only notice */}
      <div className="flex items-center gap-2">
        <Code className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
        <span className="font-mono text-[11px] uppercase tracking-wide text-faint">
          {typeLabel}
        </span>
        {item.isScriptCreated && (
          <Badge variant="neutral" className="gap-1 font-mono">
            <Lock className="h-3 w-3" aria-hidden />
            {t("variables.readOnly")}
          </Badge>
        )}
        {!canEdit && !item.isScriptCreated && (
          <Badge variant="secondary" className="gap-1">
            <Lock className="h-3 w-3" aria-hidden />
            {t("variables.noPermission")}
          </Badge>
        )}
      </div>

      {/* Name */}
      <Field label={t("variables.editDialog.nameLabel")} id="vdf-name">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          placeholder={t("variables.editDialog.namePlaceholder")}
          className="font-mono"
          disabled={isReadOnly}
          required
        />
      </Field>

      {/* Definition */}
      <Field
        label={t("variables.editDialog.definitionLabel")}
        id="vdf-definition"
        hint={t("variables.editDialog.definitionHint")}
      >
        {(control) => (
          <QlikExpressionEditor
            id={control.id}
            ariaLabel={t("variables.editDialog.definitionLabel")}
            ariaDescribedBy={control["aria-describedby"]}
            value={definition}
            onChange={handleDefinitionChange}
            placeholder={t("variables.editDialog.definitionPlaceholder")}
            minHeight="160px"
            showFx
            readOnly={isReadOnly}
          />
        )}
      </Field>

      {/* Description / Comment */}
      <Field label={t("variables.editDialog.descriptionLabel")} id="vdf-description">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder={t("variables.editDialog.descriptionPlaceholder")}
          className="min-h-[72px] resize-none"
          disabled={isReadOnly}
        />
      </Field>
    </div>
  );
}

export default VariableDetailForm;
